import type { Hydratable } from '@vimcar/mvvm/hydratable'
import type { Request } from '@vimcar/shared-utils/request'
import type { Observable } from 'rxjs'

import hydratable from '@vimcar/mvvm/hydratable'
import * as mobx from 'mobx'
import moment from 'moment'

import type { CustomAttributeValue } from '../Nodes'
import type { Values } from '../Nodes/ValuedNode'

import {
  ATTRIBUTE_NAME,
  ATTRIBUTE_EMAIL,
  ATTRIBUTE_PHONE,
  ATTRIBUTE_CAR,
  ATTRIBUTE_ENGINE,
  ATTRIBUTE_BYEAR,
  ATTRIBUTE_CARDATA,
  ATTRIBUTE_AFFECTEDCAR,
  ATTRIBUTE_NUMBERCARS,
  ATTRIBUTE_STARTSERVTIMESTAMP,
  ATTRIBUTE_ENDSERVTIMESTAMP,
  ATTRIBUTE_STARTSERVODO,
  ATTRIBUTE_ENDSERVODO,
  ATTRIBUTE_ODOTIMESTAMP,
  ATTRIBUTE_ODOMETER,
  ATTRIBUTE_TRIPSSTART,
  ATTRIBUTE_TRIPSEND,
  ATTRIBUTE_TAXOFFICE,
  ATTRIBUTE_CUSTOMERID,
  ATTRIBUTE_CLASS,
  ATTRIBUTE_DCONFIG,
  ATTRIBUTE_PRIORITY,
  ATTRIBUTE_CLIENTID_CLIENT,
  ATTRIBUTE_USERID_CLIENT,
  ATTRIBUTE_NAME_CLIENT,
  ATTRIBUTE_EMAIL_CLIENT,
  ATTRIBUTE_CHECKBOXES,
} from '../../utils/constants'
import i18n from '../../utils/i18n'

const tp = i18n.prefixWithoutTestId('Tickets')

type _Zendesk = {
  createTicket: (info: TicketInfo) => Observable<void>
  _createPayload: (info: TicketInfo) => TicketPayload
}

export type TicketInfo = {
  name?: string
  requestEmail?: string
  telephone?: string
  message?: null | string
  customValues: { [key: string]: CustomAttributeValue }
} & Values

type TicketPayload = {
  subject: string
  priority: string
  group: number
  tags?: string[]
  name: string
  requestEmail: string
  publicComment: string
  privateComment: string
}

// @ts-expect-error@2314
export type Zendesk = Hydratable<_Zendesk>

export type ZendeskOptions = {
  request: Request
  zendeskBaseUrl: string
}

const formatValue = (value: unknown) => {
  switch (typeof value) {
    case 'string':
      return value
    case 'number':
      return value.toString()
    case 'object':
      if (!value) {
        return undefined
      } else if (value instanceof Date) {
        return moment(value).format('dddd DD.MM.YYYY HH:mm')
      } else if (value instanceof Array) {
        return `\n${value.map((v) => `- ${v}`).join('\n')}`
      } else if (typeof value.toString === 'function') {
        return value.toString()
      } else {
        return undefined
      }
    default:
      return undefined
  }
}

const getAttribute = (data, key) => {
  const value =
    (data.customValues && data.customValues[key]) ||
    (data.attrs && data.attrs.get(key)) ||
    data[key]
  return value ? formatValue(value) : null
}

const tryAppendAttribute = (data, key, label) => {
  const value = getAttribute(data, key)
  if (label && value) return `${label}: ${value}\n`
  if (value) return value
  return null
}

const mkZendesk = hydratable(({ request, zendeskBaseUrl }: ZendeskOptions) => {
  const zendesk = mobx.observable({
    createTicket: (payload) => {
      const body = zendesk._createPayload(payload)
      return request(`${zendeskBaseUrl}/create-ticket`, {
        method: 'POST',
        body,
      })
    },
    _createPayload: (info) => {
      const clientId = getAttribute(info, ATTRIBUTE_CLIENTID_CLIENT)
      return {
        subject: info.subject,
        // @ts-expect-error@2554
        priority: tryAppendAttribute(info, ATTRIBUTE_PRIORITY) || 'normal',
        group: info.group,
        tags: clientId
          ? (info.tags || []).concat(`HO-ClientId-${clientId}`)
          : info.tags,
        name: info.name,
        requestEmail: info.requestEmail,
        publicComment: [
          `${info.subject}\n`,
          tryAppendAttribute(info, ATTRIBUTE_NAME, tp('name')),
          tryAppendAttribute(info, ATTRIBUTE_EMAIL, tp('email')),
          tryAppendAttribute(info, ATTRIBUTE_PHONE, tp('phone')),
          tryAppendAttribute(info, ATTRIBUTE_BYEAR, tp('build-year')),
          tryAppendAttribute(info, ATTRIBUTE_CAR, tp('group')),
          tryAppendAttribute(info, ATTRIBUTE_ENGINE, tp('engine')),
          tryAppendAttribute(info, ATTRIBUTE_AFFECTEDCAR, tp('affected-car')),
          tryAppendAttribute(info, ATTRIBUTE_TAXOFFICE, tp('tax-office')),
          tryAppendAttribute(info, ATTRIBUTE_NUMBERCARS, tp('number-cars')),
          tryAppendAttribute(
            info,
            ATTRIBUTE_STARTSERVTIMESTAMP,
            tp('service-time-start'),
          ),
          tryAppendAttribute(
            info,
            ATTRIBUTE_ENDSERVTIMESTAMP,
            tp('service-time-end'),
          ),
          tryAppendAttribute(
            info,
            ATTRIBUTE_STARTSERVODO,
            tp('odometer-start'),
          ),
          tryAppendAttribute(info, ATTRIBUTE_ENDSERVODO, tp('odometer-end')),
          tryAppendAttribute(info, ATTRIBUTE_ODOMETER, tp('odometer')),
          tryAppendAttribute(info, ATTRIBUTE_ODOTIMESTAMP, tp('odometer-time')),
          tryAppendAttribute(
            info,
            ATTRIBUTE_TRIPSSTART,
            tp('trips-time-start'),
          ),
          tryAppendAttribute(info, ATTRIBUTE_TRIPSEND, tp('trips-time-end')),
          tryAppendAttribute(info, ATTRIBUTE_CHECKBOXES, tp('checkboxes')),
          `\n${info.message || ''}`,
        ]
          .filter((item) => item !== null)
          .reduce((acc, val) => acc + val),
        privateComment: [
          tryAppendAttribute(info, ATTRIBUTE_CUSTOMERID, tp('customer-id')),
          tryAppendAttribute(info, ATTRIBUTE_CLASS, tp('class')),
          tryAppendAttribute(info, ATTRIBUTE_DCONFIG, tp('dongle-config')),
          tryAppendAttribute(info, ATTRIBUTE_CARDATA, tp('car-data')),
          tryAppendAttribute(info, ATTRIBUTE_CLIENTID_CLIENT, tp('client-id')),
          tryAppendAttribute(info, ATTRIBUTE_USERID_CLIENT, tp('user-id')),
          tryAppendAttribute(info, ATTRIBUTE_NAME_CLIENT, tp('name')),
          tryAppendAttribute(info, ATTRIBUTE_EMAIL_CLIENT, tp('email')),
        ]
          .filter((item) => item !== null)
          .reduce((acc, val) => acc + val),
      }
    },
  })
  return zendesk
}, 'Zendesk')

export default mkZendesk
